import React, { useMemo, useRef } from 'react';
import { prop } from 'ramda';

import { ImageSupportedWebp } from '@interlayer/js/app/components/common/ImageSupportWebp/ImageSupportedWebp';
import { EnterDrawBare } from '@interlayer/js/app/components/main/EnterDraw/EnterDraw';

import { useUserSelector } from '@core/js/store/hooks';
import { clsb } from '@core/js/utils/classNames.util';

import { useDetectImageColor } from '@interlayer/js/app/hooks/useDetectImageColor';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { ENTER_DRAW_ACTION } from '../../constants';
import { TImageBannerProps } from '../../types';

import style from './ImageBanner.scss';

export const ImageBanner: React.FC<TImageBannerProps> = ({
    index,
    item,
    isHiddenDescription,
    classNames = {},
    handleClickOnBanner
}) => {
    const {
        description,
        imageConfig,
        centerDescription,
        images: { userWebpImage, guestWebpImage, userFallbackImage, guestFallbackImage }
    } = item;
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const buildCls = clsb([style, classNames]);
    const imageRef = useRef(null);
    const bottomColor = useDetectImageColor({ imageRef });
    const colorDescription = item.colorDescription || bottomColor;
    const descriptionStyle = {
        ...item.sizeDescription && { fontSize: item.sizeDescription },
        ...colorDescription && { backgroundColor: colorDescription }
    };

    const { webpImage, image } = useMemo(() => {
        if (isLoggedIn) {
            return {
                webpImage: userWebpImage || guestWebpImage,
                image: userFallbackImage || guestFallbackImage
            };
        }

        return {
            webpImage: guestWebpImage || userWebpImage,
            image: guestFallbackImage || userFallbackImage
        };
    }, [guestFallbackImage, guestWebpImage, userFallbackImage, userWebpImage, isLoggedIn]);

    return (
        <div data-testid="ImageBanner" className={style.wrapper}>
            <div
                className={clsx(buildCls(['imageWrap']))}
            >
                <ImageSupportedWebp
                    image={image}
                    webpImage={webpImage}
                    alt="Banner"
                    imageRef={imageRef}
                    className={clsx(buildCls(['image']), { [style.isRoundCorners]: isHiddenDescription || !description })}
                    {...handleClickOnBanner && { onClick: handleClickOnBanner }}
                />
            </div>

            {imageConfig.action === ENTER_DRAW_ACTION && (
                <EnterDrawBare
                    type="promo"
                    index={index}
                    isParseConfig
                    className={buildCls(['btnDraw'])}
                    {...imageConfig}
                />
            )}

            {!isHiddenDescription && (
                <div
                    data-testid="ImageBanner_description"
                    style={descriptionStyle}
                    className={buildCls(['description'])}
                >
                    <span
                        className={clsx(buildCls(['descriptionText']), { [style.isCenter]: centerDescription })}
                        dangerouslySetInnerHTML={{ __html: description || '' }}
                    />
                </div>
            )}
        </div>
    );
};
